/**
 * 🔒 The print region model as a projection a screen and a PDF both render
 * (piece 27; decision 0409). An accounting document's page draws these regions
 * in its view mode and the PDF renders the same ten, so the screen is a second
 * renderer over one model rather than a second design.
 *
 * 🔨 Region 8 has its own print column set, which differs from the on-screen
 * Item Table on purpose: print has no Account column, and the row number, the
 * manufacturer's part number and the discount do not exist on screen. The
 * projection is built here, never re-read off the screen's markup.
 */
import { t, type StringKey } from '../strings';
import type { PrintFactKey } from '../print/content';
import { documentName, type NamedDocumentKind } from './documentNames';
import type { PrintTerms } from '../settings/termContainers';

/** The ten regions, in the order piece 27 fixes them. */
export const PRINT_REGIONS = [
  'Logo',
  'Billing Location',
  'Document Title & ID',
  'Document Information Table',
  'Vendor/Customer Address',
  'Deliver To',
  'Attention/Notes 1',
  'Line Items',
  'Totals & Tax',
  'Terms & Conditions/Notes',
] as const;

export type PrintRegionName = (typeof PRINT_REGIONS)[number];

/** The number a region is known by, one-based, so a caller names Region 8 rather than index 7. */
export function printRegionNumber(name: PrintRegionName): number {
  return PRINT_REGIONS.indexOf(name) + 1;
}

/** The three template families of piece 27; a document belongs to exactly one. */
export type PrintFamily = 'Transactional' | 'Field' | 'Negotiable';

const FAMILIES: Readonly<Record<string, PrintFamily>> = {
  Quote: 'Transactional',
  SalesOrder: 'Transactional',
  Invoice: 'Transactional',
  PurchaseOrder: 'Transactional',
  CreditNote: 'Transactional',
  Bill: 'Transactional',
  ChangeOrder: 'Transactional',
  Shipment: 'Transactional',
  // Every accounting document prints (piece 27): the money-out three join the same family as the money-in five.
  VendorCredit: 'Transactional',
  ExpenseReport: 'Transactional',
  // Decision 0515: the payment receipt and the change order sheet, the two documents piece 22 left open.
  Payment: 'Transactional',
  // Piece 27, decision 0223: the customer statement is the Invoice's family, with its own Region 8 column set for the running-balance ledger.
  Statement: 'Transactional',
  // 🔒 Decision 0613: the request for quotation prints on the one renderer like every
  // other document of the family, with the price column left for the vendor to fill in.
  RFQ: 'Transactional',
  // 🔒 Decision 0862: the bill of materials prints on the one renderer too, a costed list with its own column set.
  BOM: 'Transactional',
  WorkOrder: 'Field',
  // 🔒 Decision 2911: the visit's Service Report prints on a Field template of its own, beside the Work Order's.
  ServiceReport: 'Field',
  Check: 'Negotiable',
};

export class PrintFamilyError extends Error {
  constructor(documentType: string) {
    super(`${documentType} belongs to no print template family (piece 27)`);
    this.name = 'PrintFamilyError';
  }
}

/**
 * 🔒 The name a document prints under, by its stored type (decisions 0560,
 * 0638). The title is the largest thing on the sheet, so a raw enum member
 * reaches a customer at twenty-six points: the editor's sample headed itself
 * "PurchaseOrder" until a map existed. The name is the kind's own, Title Case,
 * from the one map every other screen reads; two kinds print as a sheet of a
 * different name, a Shipment as its packing slip and a Payment as its receipt.
 */
const SHEET_NAMES: Readonly<Record<string, NamedDocumentKind>> = {
  Shipment: 'PackingSlip',
  Payment: 'PaymentReceipt',
};

export function printDocumentTitle(documentType: string): string {
  return documentName(SHEET_NAMES[documentType] ?? documentType);
}

/**
 * 🔒 Every document type of one family, in the order the families' map names
 * them (decision 0658). A template duplicates onto the other types of its own
 * family and no other, because the region model differs between families.
 */
export function printDocumentTypesOf(family: PrintFamily): readonly string[] {
  return Object.entries(FAMILIES)
    .filter(([, member]) => member === family)
    .map(([documentType]) => documentType);
}

export function printFamilyOf(documentType: string): PrintFamily {
  const family = FAMILIES[documentType];
  if (!family) throw new PrintFamilyError(documentType);
  return family;
}

/** Region 8's own columns, in print order: #, Item, Description, MFR Part #, Qty, Unit Price, Discount, Amount. */
export const REGION_8_PRINT_COLUMNS = ['lineNumber', 'item', 'description', 'mfrPart', 'qty', 'unitPrice', 'discount', 'amount'] as const;

/**
 * The columns the other families and the optional Image column add. Region 8
 * is one renderer over a named column set, so a family adds a column here and
 * names it in its set rather than growing a renderer of its own.
 */
export const REGION_8_EXTRA_COLUMNS = ['image', 'unit', 'listPrice', 'sku', 'vendorPart', 'mfrReference', 'asset', 'hours', 'level', 'rate', 'invoice', 'settled', 'entryDate', 'document', 'charge', 'credit', 'balance', 'unitCost', 'extendedCost', 'workPerformed'] as const;

export type PrintLineColumn = (typeof REGION_8_PRINT_COLUMNS)[number] | (typeof REGION_8_EXTRA_COLUMNS)[number];

/** 🔨 Every column heading is a lookup key, never a literal in a renderer, so the screen and the PDF head the same column the same way (decision 0013). */
export const PRINT_COLUMN_LABEL_KEYS: Readonly<Record<PrintLineColumn, StringKey>> = {
  image: 'document.print.column.image',
  // 🔨 The unit is a field of the line and never a column of its own (decision 0560): the Qty cell stacks the quantity over it.
  unit: 'document.print.column.unit',
  // 🔒 The list price is a field of a sales line and never a column of its own (decision 1063): the Unit Price cell stacks it beneath the price.
  listPrice: 'document.print.column.listPrice',
  // 🔨 The SKU is a field of the line and never a column of its own (decision 0640): the Item cell stacks the name, the SKU and the description.
  sku: 'document.print.column.sku',
  // 🔨 The distributor's own part number is a field of a purchase line, drawn in the Item cell under the SKU (decision 0641).
  vendorPart: 'document.print.column.vendorPart',
  // 🔒 The manufacturer's reference name is a column of its own, off by default and chosen through a template's column set (T117; decision 0641).
  mfrReference: 'document.print.column.mfrReference',
  asset: 'document.print.column.asset',
  hours: 'document.print.column.hours',
  level: 'document.print.column.level',
  rate: 'document.print.column.rate',
  invoice: 'document.print.column.invoice',
  settled: 'document.print.column.settled',
  // Piece 27, decision 0223: the customer statement's running-balance ledger, which is Region 8's own column set for it.
  entryDate: 'document.print.column.entryDate',
  document: 'document.print.column.document',
  charge: 'document.print.column.charge',
  credit: 'document.print.column.credit',
  balance: 'document.print.column.balance',
  // 🔒 Decision 0862: the bill of materials' cost columns, headed as costs rather than as a customer's price.
  unitCost: 'document.print.column.unitCost',
  extendedCost: 'document.print.column.extendedCost',
  // 🔒 Decision 2911: the Service set's account of the line, typed beneath it at Complete the Visit.
  workPerformed: 'document.print.column.workPerformed',
  lineNumber: 'document.print.column.lineNumber',
  item: 'document.print.column.item',
  description: 'document.print.column.description',
  mfrPart: 'document.print.column.mfrPart',
  qty: 'document.print.column.qty',
  unitPrice: 'document.print.column.unitPrice',
  discount: 'document.print.column.discount',
  amount: 'document.print.column.amount',
};

/** The heading a column prints under, resolved through the one string lookup. */
export function printColumnLabel(column: PrintLineColumn): string {
  return t(PRINT_COLUMN_LABEL_KEYS[column]);
}

/**
 * 🔒 A column set naming both Item and Description draws ONE column of two lines
 * (decision 0560), so a set of eight columns reaches the page as seven. The rule
 * lives here and not in either renderer, because the screen and the sheet are two
 * renderers over one projection and a rule written twice is two designs
 * (decision 0409).
 */
export function printMergesDescription(columns: readonly PrintLineColumn[]): boolean {
  return columns.includes('item') && columns.includes('description');
}

/** The columns actually drawn, which is the set less the description where the Item carries it. */
export function printDrawnColumns(columns: readonly PrintLineColumn[]): readonly PrintLineColumn[] {
  return printMergesDescription(columns) ? columns.filter((column) => column !== 'description') : columns;
}

/** The heading a drawn column carries, which is the merged one where Item carries the description. */
export function printColumnHeading(column: PrintLineColumn, columns: readonly PrintLineColumn[]): string {
  return printMergesDescription(columns) && column === 'item' ? t('document.print.column.itemAndDescription') : printColumnLabel(column);
}

/** 🔨 The columns a reader adds up, which are the ones the sheet right-aligns. */
export const PRINT_NUMERIC_COLUMNS: readonly PrintLineColumn[] = ['qty', 'unitPrice', 'discount', 'amount', 'hours', 'rate', 'settled', 'charge', 'credit', 'balance', 'unitCost', 'extendedCost'];

/**
 * One printed line, already formatted by whoever built the projection; the
 * renderer writes text and never numbers. A column the family does not carry
 * is absent rather than an empty string it has to remember to supply.
 *
 * 🔨 A quantity and its unit are two fields and no projection joins them
 * (decision 0560). The Qty cell draws the quantity with the unit beneath it, and
 * a projection that wrote "2 ea" into one string leaves the renderer nothing to
 * stack. The same holds for the item, its SKU and its description, which Region
 * 8 draws as one cell stacked top to bottom (decision 0640): `item` is the
 * Item's name, `sku` its catalog SKU, and `description` the line's own text.
 */
export type PrintLine = Readonly<Partial<Record<PrintLineColumn, string>> & PrintDivider & PrintLineMarks & PrintLineImage>;

/**
 * 🔒 A mark a sales line carries on its sheet (decision 1062): `taxable` puts a
 * small T beside the line's Amount, and the table's foot then carries the
 * legend saying what it means.
 */
export interface PrintLineMarks {
  taxable?: boolean;
}

/**
 * 🔒 A line's picture (T1400; piece 29): a checklist photo prints at thumbnail size beneath the line's own
 * text, its caption the item's label. Inline `data:` address, because the renderer reaches no network.
 */
export interface PrintLineImage {
  picture?: PrintRegionImage;
}

/**
 * 🔒 A group divider on the sheet (decision 0650): a row whose `divider` is set
 * is the named band the BOM's own export prints, drawn across every column in
 * place of a line, carrying no money and no number.
 */
export interface PrintDivider {
  divider?: string;
}

/** A party's block as a window envelope shows it: a name over the address lines. */
export interface PrintParty {
  heading: string;
  name: string;
  lines: readonly string[];
}

/**
 * One row of Region 4, the Document Information Table, and one row of Region 9,
 * the totals block. 🔨 `strong` is the one row a reader's eye lands on — the
 * Total — drawn bold above a rule, so the renderer never has to recognise a
 * total by its label (decision 0560).
 */
export interface PrintFact {
  label: string;
  value: string;
  strong?: boolean;
  /**
   * 🔒 Which row of its region this fact is, so a template's own row can hide
   * it or retitle it (decision 0656). A fact written without one is drawn and
   * cannot be hidden.
   */
  key?: PrintFactKey;
}

/** The whole projection a renderer takes; a region with nothing in it is absent rather than empty. */
export interface PrintDocument {
  documentType: string;
  family: PrintFamily;
  /** Region 3: the document's own title and its number. */
  title: string;
  number: string;
  /** Region 2: the issuing location's return address. */
  issuer: PrintParty;
  /** Region 5: the other party's address. */
  recipient: PrintParty;
  /** Region 6: the ship-to, held apart from both. */
  deliverTo: PrintParty | null;
  /** Region 4. */
  information: readonly PrintFact[];
  /** Region 7, a freeform callout, distinct from Region 10's closing notes. */
  notice: string | null;
  /** Region 8. */
  lines: readonly PrintLine[];
  /** 🔒 Headings this document gives its own columns in place of the column's usual one, such as a Sent payment's Bill (T3754). */
  columnHeadings?: Partial<Record<PrintLineColumn, string>>;
  /** Region 9; absent turns the region off rather than printing a zero (piece 27's packing-slip rule). */
  totals: readonly PrintFact[] | null;
  /**
   * Region 10's closing note as a projection writes it. The one print path
   * moves it to `notes`, and the renderer draws both as the first block, so a
   * template sizes and hides the note and the clauses apart (decision 0656).
   */
  terms: readonly string[];
  /** 🔒 Region 10's first block, the document's own closing note, drawn above the clauses (decision 0656). */
  notes?: readonly string[];
  /**
   * 🔒 The Term Container's clauses in force, or the document's own text in
   * their place (decisions 0684, 0702): each section's heading before its
   * clauses. Resolved by the one print path; absent prints none.
   */
  clauses?: PrintTerms | null;
  /**
   * 🔒 The document's own terms text, which the one print path prints in place
   * of the Term Container's clauses where it is set (T125; decision 0684).
   */
  termsOverride?: string | null;
  /**
   * The signature lines Region 10 prints beneath the clause text, one per
   * active `SignatureBlockParty` row of the document's type; empty prints none.
   */
  signatureParties?: readonly string[];
  /** 🔒 The CONFIDENTIAL footer mark, where the document, its Company or its Deal carries a Confidential requirement. */
  confidential?: boolean;
  /**
   * 🔒 The Reprint corner mark's day, in the organisation's date format, where a
   * sent document is printed after its send (decision 10632; T887). Set on the
   * server by the print routes alone; a send's own copy and the page's sheet carry none.
   */
  reprintedOn?: string | null;
  /** The regions a family numbers above ten, by number: the Work Order's four and the check's two. */
  extras?: readonly PrintRegionValue[];
  /**
   * 🔒 Region 1: the organisation's logo as an inline `data:` URI
   * (decisions 0548, 0575). The renderer reaches no network, so the sheet's own
   * markup is the only place the bytes can be. 🔨 It is set on the server as
   * the sheet is rendered and never rides in the answer a browser reads — that
   * one draws the same logo through the file's ordinary read route instead.
   */
  logoDataUri?: string | null;
}

/**
 * 🔒 Region 7's text (T124; decision 0683): the notes to the customer typed on
 * the document's form, above any notice the document raises itself, one line
 * each; null where there is neither, so the region prints nothing.
 */
export function printCallout(customerNotes: string | null | undefined, notice: string | null | undefined): string | null {
  const parts = [customerNotes?.trim() || null, notice?.trim() || null].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join('\n') : null;
}

/** One region above ten, carrying the one thing it draws. */
export interface PrintRegionValue {
  regionNumber: number;
  label: string;
  value: string;
  /**
   * 🔒 The drawn signatures Region 14 prints beneath its text, each with its
   * caption (decisions 1910, 2061): the customer's, then the lead worker's.
   * Inline `data:` addresses, because the renderer reaches no network.
   */
  images?: readonly PrintRegionImage[];
  /**
   * 🔒 The photos a region prints at thumbnail size beneath its text, each with
   * its caption (decision 2911): the Service Report's notes and checklist
   * results. A PNG or a JPEG as an inline `data:` address; anything else is left out.
   */
  thumbnails?: readonly PrintRegionImage[];
}

/** One drawn picture a region prints, with the line beneath it naming who drew it. */
export interface PrintRegionImage {
  caption: string;
  dataUri: string;
}

/**
 * 🔨 A party's address lines come from `formatMailingAddressLines` in
 * `records/mailingAddress.ts` and from nowhere else (decision 0406). A second
 * mailing formatter stood here on the branch this merged: it took a country
 * name and printed it always where the one form prints a country code only
 * abroad, it knew nothing of the dependent locality, and it took a free record
 * a caller could hand the municipality to (found in review, 2026-09-15).
 *
 * A page whose facts are flat text carries those lines as a JSON array, the
 * way the Work Order already carries its action list; this reads one back and
 * answers nothing where the fact is absent or malformed.
 */
export function printPartyLines(fact: string | boolean | null | undefined): string[] {
  if (typeof fact !== 'string' || fact === '') return [];
  try {
    const parsed: unknown = JSON.parse(fact);
    return Array.isArray(parsed) ? parsed.filter((line): line is string => typeof line === 'string' && line !== '') : [];
  } catch {
    return [];
  }
}

/**
 * 🔒 A party block the server stacked through its one rule and sent as a page
 * fact (T3782): the block's name and its lines as JSON, the very block the PDF
 * prints. The sheet draws it as sent and stacks nothing of its own; an absent
 * or malformed fact answers null.
 */
export function printPartyBlock(fact: string | boolean | null | undefined): { name: string; lines: string[] } | null {
  if (typeof fact !== 'string' || fact === '') return null;
  try {
    const parsed: unknown = JSON.parse(fact);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { name, lines } = parsed as { name?: unknown; lines?: unknown };
    if (typeof name !== 'string') return null;
    return { name, lines: Array.isArray(lines) ? lines.filter((line): line is string => typeof line === 'string' && line !== '') : [] };
  } catch {
    return null;
  }
}

/**
 * 🔨 The regression piece 27 asks for: Region 8's column set intentionally
 * differs from the on-screen Item Table's, so a reconciliation of the two is a
 * change to this answer and not a tidy-up.
 */
export function region8DiffersFromScreen(screenColumns: readonly string[]): boolean {
  const print = new Set<string>(REGION_8_PRINT_COLUMNS);
  const screen = new Set(screenColumns);
  if (print.size !== screen.size) return true;
  for (const column of print) if (!screen.has(column)) return true;
  return false;
}
